import fastDiff from 'fast-diff';
import { isPhrasingContent } from '../../helpers/dom/content';
import { addFillerIfContainerEmpty } from '../../helpers/dom/contentMutations';
import { findAncestor } from '../../helpers/dom/node';
import { createRangeWithTextRange, createTextRangeWithRange, TextRange } from '../../helpers/dom/range';
import { patchTextContent } from '../../helpers/dom/text';
import { expandWhitespace } from '../../helpers/textToHtml';
import { DEFAULT_TEXT_UNDER_CURSOR, TextUnderCursor } from '../../helpers/textUnderCursor';
import { BaseService } from '../base/service';
import { TextUnderCursorRange, TextUnderCursorService } from '../text/textUnderCursorService';
import { ElementSelectionService } from './elementSelectionService';

export function createElementParagraphTextUnderCursorService(
  selectionService: ElementSelectionService,
): TextUnderCursorService {
  return new ElementParagraphTextUnderCursorService(selectionService);
}

interface ElementParagraphTextUnderCursorState {
  container: Node;
  range: Range;
  isBackward: boolean;
}

const MAX_DIFF_COUNT = 5;

export class ElementParagraphTextUnderCursorService extends BaseService implements TextUnderCursorService {
  constructor(selectionService: ElementSelectionService) {
    super(selectionService);

    this.selectionService = selectionService;
  }

  private readonly selectionService: ElementSelectionService;

  private get state(): ElementParagraphTextUnderCursorState | undefined {
    const { container, selection } = this.selectionService;
    if (!container || !selection) {
      return undefined;
    }

    const { anchorNode, focusNode, range, isBackward } = selection;
    const anchorContainer = findAncestor(anchorNode, node => !isPhrasingContent(node), container);
    const focusContainer = findAncestor(focusNode, node => !isPhrasingContent(node), container);

    if (!anchorContainer || !focusContainer || anchorContainer !== focusContainer) {
      return undefined;
    }

    return {
      container: focusContainer,
      range,
      isBackward,
    };
  }

  get isActive() {
    return this.selectionService.isActive;
  }

  get textUnderCursor() {
    const { state } = this;
    if (!state) {
      return DEFAULT_TEXT_UNDER_CURSOR;
    }

    const { container, range } = state;
    const textRange = createTextRangeWithRange(container, range);
    if (!textRange) {
      return DEFAULT_TEXT_UNDER_CURSOR;
    }

    return {
      text: container.textContent || '',
      anchorOffset: textRange.startIndex,
      focusOffset: textRange.endIndex,
    };
  }

  getBoundingClientRect(textRange?: TextUnderCursorRange): ClientRect | undefined {
    const { state } = this;
    if (!state) {
      return undefined;
    }

    const range = textRange
      ? createRangeWithTextRange(state.container, {
          startIndex: textRange.startOffset,
          endIndex: textRange.endOffset,
        })
      : state.range;
    if (!range) {
      return undefined;
    }

    return range.getBoundingClientRect();
  }

  setTextUnderCursor(textUnderCursor: Readonly<TextUnderCursor>): void {
    const { state } = this;
    if (!state) {
      return;
    }

    const { container, range: prevRange } = state;
    const prevTextRange = createTextRangeWithRange(container, prevRange);
    const prevText = container.textContent || '';

    const { anchorOffset, focusOffset } = textUnderCursor;
    const isBackward = anchorOffset > focusOffset;
    const startIndex = isBackward ? focusOffset : anchorOffset;
    const endIndex = isBackward ? anchorOffset : focusOffset;
    const textRange = { startIndex, endIndex };
    const { text } = textUnderCursor;

    // Try to make the least number of text changes required, so that we can keep existing DOM elements.
    const diffCursorInfo = prevTextRange ? createDiffCursorInfo(prevTextRange, textRange) : focusOffset;
    const diffs = fastDiff(prevText, text, diffCursorInfo);
    const didApplyDiffs = diffs.length <= MAX_DIFF_COUNT && patchTextContent(container, diffs);

    // If the patch failed, fall back to replacing all text.
    if (!didApplyDiffs) {
      // TODO: Fix previously collapsed whitespace now being expanded.
      container.textContent = expandWhitespace(text);
    }

    addFillerIfContainerEmpty(container);

    const range = createRangeWithTextRange(container, textRange);
    if (!range) {
      return;
    }

    this.selectionService.selectWithRange(range, isBackward);
  }
}

function createDiffCursorInfo(prevTextRange: TextRange, nextTextRange: TextRange): fastDiff.CursorInfo {
  return {
    oldRange: createDiffRange(prevTextRange),
    newRange: createDiffRange(nextTextRange),
  };
}

function createDiffRange(
  textRange: TextRange,
): fastDiff.CursorInfo['oldRange'] | fastDiff.CursorInfo['newRange'] {
  return { index: textRange.startIndex, length: textRange.endIndex - textRange.startIndex };
}
